/** Shared "Add customer" dialog — used from the sidebar (quick walk-in add)
 *  and the Customers page. On save it opens the new customer's profile. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const EMPTY = { name: "", email: "", phone: "", city: "", note: "" };

export const AddCustomerDialog = ({ open, onOpenChange, onCreated }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/host/crm/customers`, {
        method: "POST", headers, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const { customer_id } = await res.json();
      toast.success("Customer added");
      onOpenChange(false);
      setForm(EMPTY);
      if (onCreated) onCreated(customer_id);
      navigate(`/host/customers/${customer_id}`);
    } catch { toast.error("Couldn't add customer"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle>Add a customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="c-name">Name *</Label>
            <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus data-testid="add-customer-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-customer-email" />
            </div>
            <div>
              <Label htmlFor="c-phone">Phone</Label>
              <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-customer-phone" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-city">City</Label>
            <Input id="c-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="add-customer-city" />
          </div>
          <div>
            <Label htmlFor="c-note">First note (optional)</Label>
            <Textarea id="c-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="add-customer-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="add-customer-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomerDialog;
